import { ModalState } from './modal-state';
import type { ErrorMessageCodes } from './error-message-codes';
import type { AppDispatch, GetState } from 'tradera/state/configure-store';
import { completed, inProgress, setError, reset, startAuth } from './reducer';
import { webApiClient } from 'tradera/utils/http';
import type {
    BankIdAuthenticationBeginApiRequest,
    BankIdAuthenticationBeginApiResponse,
    BankIdAuthenticationProgressApiResponse,
    BankIdOnFileAuthenticationBeginApiRequest,
    BankIdOnFileAuthenticationBeginApiResponse,
    BeginBankIdSignatoryAuthenticationApiRequest
} from 'tradera/state/services/types/webapi-auth-generated';
import { showSuccessToast } from 'tradera/services/toasts';
import { logger } from 'packages/logger';
import type { AxiosError } from 'axios';
import { type BankIdBeginWithIdApiRequest } from 'tradera/state/services/types/webapi-member-generated';
import qs from 'qs';

type OnError = (message?: ErrorMessageCodes) => void;
type OnComplete = (
    bankIdOrderRef?: string | null,
    autoStartToken?: string | null
) => void;

const sleep = (delay: number) =>
    new Promise((resolve) => setTimeout(resolve, delay));

export const startAuthAndOpenModal = (
    onComplete: OnComplete,
    onError: OnError,
    purpose: string,
    contactSsn?: string,
    organizationNumber?: string
) => {
    return async (dispatch: AppDispatch) => {
        dispatch(reset());
        dispatch(startAuth({ purpose }));

        try {
            const { data } = contactSsn
                ? await webApiClient.post<
                      BankIdAuthenticationBeginApiResponse,
                      BeginBankIdSignatoryAuthenticationApiRequest
                  >('/auth/bankid/authenticate-signatory', {
                      purpose,
                      organizationNumber,
                      contactSsn
                  })
                : await webApiClient.post<
                      BankIdAuthenticationBeginApiResponse,
                      BankIdAuthenticationBeginApiRequest
                  >('/auth/bankid/authenticate', { purpose });

            const errorCode = data.responseStatus
                ?.errorCode as ErrorMessageCodes;
            if (errorCode) {
                dispatch(setError(errorCode));
                onError(errorCode);

                logger(`BankId: received error code ${errorCode}`);
                return;
            }

            dispatch(
                inProgress({
                    autoStartToken: data?.autoStartToken,
                    qrData: data?.qrData,
                    bankIdOrderRef: data?.bankIdOrderRef,
                    contactSsn: contactSsn,
                    organizationNumber: organizationNumber
                })
            );
            dispatch(
                bankIdPollProgress(
                    onComplete,
                    onError,
                    contactSsn,
                    organizationNumber
                )
            );
        } catch (error) {
            dispatch(setError(undefined));
            onError(undefined);

            const maybeAxiosError = error as AxiosError;
            logger(`BankId: received exception ${maybeAxiosError?.message}`);
        }
    };
};

export const startAuthWithIdAndOpenModal = (
    onComplete: OnComplete,
    onError: OnError,
    purpose: string,
    memberId: number
) => {
    return async (dispatch: AppDispatch) => {
        dispatch(reset());
        dispatch(startAuth({ purpose, memberId }));

        try {
            const { data } = await webApiClient.post<
                BankIdAuthenticationBeginApiResponse,
                BankIdBeginWithIdApiRequest
            >('/member/profile/bankId-recovery-with-id', {
                memberId
            });

            const errorCode = data.responseStatus
                ?.errorCode as ErrorMessageCodes;
            if (errorCode) {
                dispatch(setError(errorCode));
                onError(errorCode);

                logger(`BankId: received error code ${errorCode}`);
                return;
            }

            dispatch(
                inProgress({
                    autoStartToken: data?.autoStartToken,
                    qrData: data?.qrData,
                    bankIdOrderRef: data?.bankIdOrderRef
                })
            );
            dispatch(
                bankIdPollProgress(
                    onComplete,
                    onError,
                    undefined /* contactSsn */,
                    undefined /* organizationNumber */,
                    memberId
                )
            );
        } catch (error) {
            dispatch(setError(undefined));
            onError(undefined);

            const maybeAxiosError = error as AxiosError;
            logger(`BankId: received exception ${maybeAxiosError?.message}`);
        }
    };
};

export const startAuthOnFile = () => {
    return async (dispatch: AppDispatch, getState: GetState) => {
        const { bankId } = getState();
        dispatch(startAuth({ purpose: bankId.purpose }));

        let autoStartToken: string | null = null;
        try {
            const { data } = await webApiClient.post<
                BankIdOnFileAuthenticationBeginApiResponse,
                BankIdOnFileAuthenticationBeginApiRequest
            >('/auth/bankid/authenticate-on-file', { purpose: bankId.purpose });

            const errorCode = data.responseStatus
                ?.errorCode as ErrorMessageCodes;
            if (errorCode) {
                dispatch(setError(errorCode));
                logger(`BankId on file: received error code ${errorCode}`);
                return;
            }

            autoStartToken = data?.autoStartToken ?? null;
            const bankIdOrderRef = data?.bankIdOrderRef ?? undefined;
            dispatch(
                inProgress({
                    autoStartToken,
                    bankIdOrderRef
                })
            );
        } catch (error) {
            dispatch(setError(undefined));

            const maybeAxiosError = error as AxiosError;
            logger(
                `BankId on file: received exception ${maybeAxiosError?.message}`
            );
        }

        return autoStartToken;
    };
};

/**
 * poll touchweb/webapi to see check if the user has made their bank id auth.
 * this might take some time since the user needs to open up their mobile app with bank id
 */
const bankIdPollProgress =
    (
        onComplete: OnComplete,
        onError: OnError,
        contactSsn?: string,
        organizationNumber?: string,
        memberId?: number
    ) =>
    async (dispatch: AppDispatch, getState: GetState) => {
        const state = getState();
        const { modalState, bankIdOrderRef, errorCode } = state.bankId;

        if (modalState === ModalState.ERROR) {
            onError(errorCode ?? undefined);
            return;
        }

        if (modalState !== ModalState.IN_PROGRESS) {
            return;
        }

        if (!bankIdOrderRef) {
            await sleep(3000);
            dispatch(
                bankIdPollProgress(
                    onComplete,
                    onError,
                    contactSsn,
                    organizationNumber
                )
            );

            return;
        }

        try {
            const { data } = contactSsn
                ? await webApiClient.get<BankIdAuthenticationProgressApiResponse>(
                      `/auth/bankid/member-identification-progress-signatory?bankIdOrderRef=${bankIdOrderRef}&organizationNumber=${organizationNumber}&contactSsn=${contactSsn}`
                  )
                : memberId
                  ? await webApiClient.get<BankIdAuthenticationProgressApiResponse>(
                        `/member/profile/bankId-progress-with-id?${qs.stringify(
                            {
                                bankIdOrderRef,
                                memberId
                            }
                        )}`
                    )
                  : await webApiClient.get<BankIdAuthenticationProgressApiResponse>(
                        `/auth/bankid/progress?bankIdOrderRef=${bankIdOrderRef}`
                    );

            const errorCode = data.responseStatus
                ?.errorCode as ErrorMessageCodes;

            if (errorCode) {
                dispatch(setError(errorCode));
                onError(errorCode);
                return;
            }

            if (data?.isCompleted) {
                dispatch(completed());
                showSuccessToast('bankIdModal_verificationCompletedTitle');
                onComplete(bankIdOrderRef);
                return;
            }

            dispatch(
                inProgress({
                    qrData: data?.qrData
                })
            );

            await sleep(3000);
            dispatch(
                bankIdPollProgress(
                    onComplete,
                    onError,
                    contactSsn,
                    organizationNumber,
                    memberId
                )
            );
        } catch {
            dispatch(setError(undefined));
            onError(undefined);
        }
    };
